"use client";

import React, { useCallback } from "react";
import { Clock, Plus, ExternalLink } from "lucide-react";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { TicketTimeEntriesTable } from "../TicketTimeEntriesTable";
import type { Ticket } from "@/lib/types/database";

interface TimeEntriesTabProps {
  item: Ticket;
}

interface CreateTimeEntryModalData {
  ticketId: string;
  ticketTitle?: string;
  contactId?: string;
  contactName?: string;
  companyId?: string;
  companyName?: string;
  item: Ticket;
}

/**
 * Build modal data for Create Time Entry from a ticket (matches communication-actions shape).
 */
function buildCreateTimeEntryData(item: Ticket): CreateTimeEntryModalData {
  const ticketTitle =
    item.title ||
    (item.ticket_number != null ? `Ticket #${item.ticket_number}` : undefined);

  const contactName =
    item.contact_name ||
    item.contacts?.name ||
    item.contacts?.email ||
    undefined;

  const companyName = item.company_name || item.companies?.name || undefined;

  return {
    ticketId: item.id,
    ticketTitle,
    contactId: item.contact_id ?? undefined,
    contactName,
    companyId: item.company_id,
    companyName,
    item,
  };
}

/**
 * Time Entries Tab Component
 * Uses the global Create Time Entry modal (centered on desktop, drawer on mobile).
 */
export const TimeEntriesTab = React.memo(({ item }: TimeEntriesTabProps) => {
  const router = useRouter();
  const { openModal } = useGlobalModalStack();

  const handleViewAllTimeEntries = useCallback(() => {
    router.push(`/time-tracking?ticketId=${item.id}`);
  }, [router, item.id]);

  const handleAddTimeEntry = useCallback(() => {
    // createQuick ('create-time-entry') is the id CreateTimeEntryModal binds and
    // is mounted globally in GlobalQuickActionModals. The old `create`
    // ('create-time_entries') id is registered but has NO mounted renderer, so
    // opening it pushed an invisible stack entry and the create modal never
    // painted above the ticket detail modal. See ACTION_MODAL_MOUNTS rule.
    openModal(MODAL_IDS.timeEntries.createQuick, buildCreateTimeEntryData(item));
  }, [openModal, item]);

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-card-foreground">Time Entries</h3>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleAddTimeEntry}
            type="button"
          >
            <Plus className="w-4 h-4" />
            Add Time Entry
          </Button>
          <Button variant="outline" size="sm" onClick={handleViewAllTimeEntries} className="gap-2">
            <ExternalLink className="w-4 h-4" />
            View All Time Entries
          </Button>
        </div>
      </div>

      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">Time tracking entries for this ticket</div>

      <TicketTimeEntriesTable ticketId={item.id} ticketNumber={item.ticket_number?.toString()} />
    </div>
  );
});

TimeEntriesTab.displayName = "TimeEntriesTab";
